import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { Layout } from "../layout/Layout";
import { BlockKbPage } from "@/components/kb/BlockKbPage";
import { PaamonHomePage } from "../pages/PaamonHomePage";
import { PaamonDashboardPage } from "../pages/PaamonDashboardPage";
import { PaamonWaitingListPage } from "../pages/PaamonWaitingListPage";
import { PaamonMarketingContactsPage } from "../pages/PaamonMarketingContactsPage";
import { PaamonMunicipalityPage } from "../pages/PaamonMunicipalityPage";
import { PaamonPhoneScriptsPage } from "../pages/PaamonPhoneScriptsPage";
import { ClinicHomePage } from "../pages/ClinicHomePage";
import { ClinicDocumentsPage } from "../pages/ClinicDocumentsPage";
import { ShirDerechHomePage, ShirDerechResponsesPage } from "../pages/ShirDerechPages";
import { useApi } from "@/lib/api";

export const CRM = () => (
  <Layout>
    <Routes>
      <Route path="/" element={<PaamonHomePage />} />

      {/* Paamon — bespoke Structured pages */}
      <Route path="/paamon" element={<PaamonHomePage />} />
      <Route path="/paamon/dashboard" element={<PaamonDashboardPage />} />
      <Route path="/paamon/waiting-list" element={<PaamonWaitingListPage />} />
      <Route path="/paamon/marketing-contacts" element={<PaamonMarketingContactsPage />} />
      <Route path="/paamon/municipality" element={<PaamonMunicipalityPage />} />
      <Route path="/paamon/phone-scripts" element={<PaamonPhoneScriptsPage />} />

      {/* Clinic — bespoke Structured pages */}
      <Route path="/clinic" element={<ClinicHomePage />} />
      <Route path="/clinic/documents" element={<ClinicDocumentsPage />} />

      {/* Shir-Derech — questionnaire responses (public forms live at
          /form/shir-derech/* and are rendered outside the auth gate in main.tsx) */}
      <Route path="/shir-derech" element={<ShirDerechHomePage />} />
      <Route path="/shir-derech/opening" element={<ShirDerechResponsesPage key="opening" kind="opening" />} />
      <Route path="/shir-derech/closing" element={<ShirDerechResponsesPage key="closing" kind="closing" />} />

      {/* Knowledge — Classic. One catch-all hits every page_content row at
          slug `kb-doc/...`. Slug-rename + redirect lookup handled inside
          BlockKbPage via the /api/kb catchall (MJOS-104 / DEM-24). */}
      <Route path="/kb-doc/*" element={<BlockKbPage />} />

      {/* DEM-24 legacy-URL fallback. Any unmatched URL is treated as a
          potential old slug (`/paamon/tasks` → `paamon/tasks`) and looked
          up against page_redirects via /api/kb. On a hit we navigate to
          the new slug; on a miss we land on the home page. */}
      <Route path="*" element={<LegacyRedirect />} />
    </Routes>
  </Layout>
);

function LegacyRedirect() {
  const api = useApi();
  const navigate = useNavigate();
  const location = useLocation();
  const [pending, setPending] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const candidate = location.pathname.replace(/^\//, "").replace(/\/$/, "");
    if (!candidate) {
      navigate("/", { replace: true });
      return;
    }
    (async () => {
      try {
        const res = await api(`/api/kb/${candidate}`);
        if (cancelled) return;
        if (res.ok) {
          const body = (await res.json()) as { redirect_to?: string };
          if (cancelled) return;
          if (body.redirect_to) {
            navigate(`/${body.redirect_to}`, { replace: true });
            return;
          }
        }
        navigate("/", { replace: true });
      } catch {
        if (!cancelled) navigate("/", { replace: true });
      } finally {
        if (!cancelled) setPending(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // api/navigate identity changes per render — keying off pathname alone
    // avoids an infinite re-fetch loop here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  if (!pending) return null;
  return (
    <div className="flex h-full items-center justify-center p-6 text-sm text-stone-500">
      <Loader2 className="me-2 h-4 w-4 animate-spin" />
      טוען…
    </div>
  );
}
